<template>
  <div
    v-if="isOnline"
    class="app-navbar-actions"
    :class="{ 'app-navbar-actions--labeled': showLabels }"
  >
    <v-btn
      :icon="!showLabels"
      variant="text"
      color="white"
      size="small"
      density="comfortable"
      class="app-navbar-actions__btn"
      aria-label="Notificaciones"
      :aria-expanded="previewOpen"
      @click="toggleNotifications"
    >
      <v-badge
        :model-value="showNotificationBadge"
        :content="unreadCount > 99 ? '99+' : unreadCount"
        color="error"
      >
        <v-icon size="22">mdi-bell-outline</v-icon>
      </v-badge>
      <span v-if="showLabels" class="app-navbar-actions__label">Notificaciones</span>
    </v-btn>

    <v-btn
      v-if="hasGroupChats"
      :icon="!showLabels"
      variant="text"
      color="white"
      size="small"
      density="comfortable"
      class="app-navbar-actions__btn"
      :aria-label="totalUnread > 0 ? `Chats de grupo, ${totalUnread} sin leer` : 'Chats de grupo'"
      @click="openGroupChats"
    >
      <v-badge
        :model-value="totalUnread > 0"
        :content="totalUnread > 99 ? '99+' : totalUnread"
        color="error"
      >
        <v-icon size="22">mdi-forum-outline</v-icon>
      </v-badge>
      <span v-if="showLabels" class="app-navbar-actions__label">Chats</span>
    </v-btn>

    <HelpChatLauncher variant="nav" />

    <v-menu location="bottom end">
      <template #activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          icon
          variant="text"
          color="white"
          size="small"
          density="comfortable"
          class="app-navbar-actions__btn app-navbar-actions__more"
          aria-label="Más opciones"
        >
          <v-icon size="22">mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-list density="compact" min-width="200">
        <v-list-item
          prepend-icon="mdi-crown-outline"
          title="Planes"
          to="/account?section=plans"
        />
        <v-list-item
          prepend-icon="mdi-book-open-variant"
          title="Guía"
          to="/how-it-works"
        />
        <v-list-item
          prepend-icon="mdi-heart"
          title="Donaciones"
          to="/donations"
        />
        <v-list-item
          v-if="showAdmin"
          prepend-icon="mdi-shield-account"
          title="Admin"
          to="/admin"
        />
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'

import HelpChatLauncher from '@/components/help/HelpChatLauncher.vue'
import { useConnectionStatus } from '@/composables/useConnectionStatus'
import { useAuthStore } from '@/stores/auth'
import { useGroupChatsStore } from '@/stores/groupChats'
import { useHelpChatStore } from '@/stores/helpChat'
import { useNotificationsStore } from '@/stores/notifications'

export default defineComponent({
  name: 'AppNavbarActions',

  components: {
    HelpChatLauncher,
  },

  setup() {
    const isMobile = useDisplay().mobile
    const showLabels = computed(() => !isMobile.value)
    const { isOnline } = useConnectionStatus()
    const authStore = useAuthStore()
    const helpChatStore = useHelpChatStore()
    const groupChatsStore = useGroupChatsStore()
    const notificationsStore = useNotificationsStore()
    const { unreadCount, previewOpen } = storeToRefs(notificationsStore)
    const { hasGroupChats, totalUnread } = storeToRefs(groupChatsStore)
    const showAdmin = computed(() => authStore.isAdmin && !authStore.isImpersonating)
    const isGuestUser = computed(() => authStore.userType === 'guest')
    const showNotificationBadge = computed(
      () => !isGuestUser.value && unreadCount.value > 0,
    )

    const loadGroupChats = () => {
      if (authStore.userType !== 'registered') {
        groupChatsStore.reset()
        return
      }
      void groupChatsStore.load()
    }

    const openGroupChats = () => {
      helpChatStore.close()
      notificationsStore.closePreview()
      void groupChatsStore.load()
      groupChatsStore.open()
    }

    const toggleNotifications = () => {
      if (notificationsStore.previewOpen) {
        notificationsStore.closePreview()
        return
      }
      helpChatStore.close()
      groupChatsStore.close()
      notificationsStore.openPreview()
      if (authStore.userType === 'registered') {
        void notificationsStore.loadUnread()
      }
    }

    onMounted(loadGroupChats)

    watch(
      () => authStore.currentUser?.userId,
      () => {
        loadGroupChats()
      },
    )

    return {
      isOnline,
      showLabels,
      unreadCount,
      showNotificationBadge,
      previewOpen,
      hasGroupChats,
      totalUnread,
      showAdmin,
      openGroupChats,
      toggleNotifications,
    }
  },
})
</script>

<style scoped>
.app-navbar-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
}

.app-navbar-actions--labeled {
  gap: 4px;
}

.app-navbar-actions:not(.app-navbar-actions--labeled) {
  gap: 8px;
}

.app-navbar-actions:not(.app-navbar-actions--labeled) .app-navbar-actions__btn {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  max-width: 36px;
  height: 36px;
  padding: 0;
}

.app-navbar-actions--labeled .app-navbar-actions__btn {
  height: 36px;
  padding-inline: 10px;
  text-transform: none;
  letter-spacing: normal;
}

.app-navbar-actions__label {
  margin-left: 6px;
  font-size: 0.875rem;
  font-weight: 600;
}

.app-navbar-actions :deep(.help-nav) {
  margin-left: 0;
}

.app-navbar-actions:not(.app-navbar-actions--labeled) :deep(.help-nav) {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  max-width: 36px;
  height: 36px;
  padding: 0;
  gap: 0;
}

.app-navbar-actions:not(.app-navbar-actions--labeled) :deep(.help-nav__label) {
  display: none;
}

.app-navbar-actions__more,
.app-navbar-actions--labeled .app-navbar-actions__more {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  max-width: 36px;
  height: 36px;
  padding: 0;
}
</style>
